import React, {
    createContext,
    useContext,
} from 'react';
import type { ReactNode } from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { Experiment, Source } from '@amplitude/experiment-js-client';
import { useSelector } from 'react-redux';

import { experimentEligibilitySelectors, variantsSelectors } from 'wanderu-analytics';
import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';

import type { EnrichmentPlugin, BrowserConfig, Event } from '@amplitude/analytics-types';
import type { ExperimentClient } from '@amplitude/experiment-js-client';

const logger = Logger.getLogger('AmplitudeProvider');

interface AmplitudeContextType {
    amplitude: typeof amplitude;
    experiment?: ExperimentClient;
}

interface AmplitudeProviderProps {
    children: ReactNode;
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(
    undefined
);

class SnowplowIdsPlugin implements EnrichmentPlugin {
    name = 'snowplow-ids-plugin';

    type = 'enrichment' as const;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, class-methods-use-this
    async setup(config: BrowserConfig) {
        return undefined;
    }

    // eslint-disable-next-line class-methods-use-this
    async execute(event: Event) {
        const newEvent = event;

        const {
            domainUserId,
            domainSessionId,
            domainSessionIdx,
        } = SnowplowService.getDomainUserInfo() as {
            domainUserId: string;
            domainSessionId: string;
            domainSessionIdx: number;
        };

        const networkUserId = SnowplowService.getNetworkUserId() as string;

        if (!newEvent.event_properties) {
            newEvent.event_properties = {};
        }

        newEvent.event_properties.domain_session_id = domainSessionId;
        newEvent.event_properties.domain_session_idx = domainSessionIdx;
        newEvent.event_properties.domain_user_id = domainUserId;
        newEvent.event_properties.network_user_id = networkUserId;

        return newEvent;
    }
}

let experiment: ExperimentClient | undefined;

export function AmplitudeProvider({ children }: AmplitudeProviderProps) {
    const variants = useSelector(variantsSelectors.selectEntities);
    const experimentEligibility = useSelector(
        experimentEligibilitySelectors.selectExperimentEligibility
    );

    const apiKey = appSettings?.config?.amplitude?.apiKey;

    if (!apiKey || typeof window === 'undefined') {
        const missing = !apiKey
            ? 'apiKey'
            : 'window';
        logger.event('Amplitude', `${missing} not found`);

        return ( // return the AmplitudeProvider early with undefined values if we don't have an apiKey or window
            <AmplitudeContext.Provider value={{ amplitude, experiment }}>
                {children}
            </AmplitudeContext.Provider>
        );
    }

    amplitude.init(apiKey, { // Browser Analytics https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#configure-the-sdk
        appVersion: appSettings?.env?.build?.version,
        autocapture: {
            attribution: {
                resetSessionOnNewCampaign: true,
            },
            elementInteractions: true,
            fileDownloads: true,
            formInteractions: true,
            pageViews: {
                trackOn: () => {
                    // disabling auto page views for search pages since we're going to manually track them
                    return !window.location.pathname.includes('depart') && !window.location.pathname.includes('return');
                },
            },
            sessions: true,
        },
        serverUrl: appSettings?.config?.amplitude?.serverUrl,
    })
        .promise
        .then(
            (result) => {
                const deviceId = amplitude.getDeviceId() as string;

                logger.event('Amplitude', 'loaded', { deviceId, result });

                amplitude.add(new SnowplowIdsPlugin()); // Add snowplow ids to every Amplitude event

                SnowplowService.addGlobalContexts([{
                    data: {
                        ampClientId: deviceId,
                    },
                    schema: 'iglu:dev.amp.snowplow/amp_id/jsonschema/1-0-0',
                }]);
            },
            (error) => {
                const { deviceId } = experimentEligibility;

                logger.event('Amplitude', 'failed', { deviceId, error });
            }
        );

    if (experimentEligibility?.isEligibleForAssignment !== null) {
        const {
            ineligibilityReason,
            isEligibleForAssignment,
            userProperties,
        } = experimentEligibility;

        const {
            asn,
            cfBotScore,
            ipAddress,
            ja4,
            userAgent,
        } = userProperties || {};

        const identifyEvent = new amplitude.Identify();

        identifyEvent.set('is_eligible_for_assignment', isEligibleForAssignment);
        if (cfBotScore) identifyEvent.set('cloudflare_bot_score', cfBotScore);
        if (ineligibilityReason) identifyEvent.set('ineligibility_reason', ineligibilityReason);
        if (asn) identifyEvent.set('asn', asn);
        if (ipAddress) identifyEvent.set('ip_address', ipAddress);
        if (ja4) identifyEvent.set('ja4', ja4);
        if (userAgent) identifyEvent.set('user_agent', userAgent);

        amplitude.identify(identifyEvent);
    }

    if (!experiment) {
        // in the client, we only want to create the ExperimentClient once
        experiment = Experiment.initializeWithAmplitudeAnalytics(apiKey, {
            initialVariants: { ...variants },
            source: Source.InitialVariants,
        });
    }

    return (
        <AmplitudeContext.Provider value={{ amplitude, experiment }}>
            {children}
        </AmplitudeContext.Provider>
    );
}

export function useAmplitude() {
    const context = useContext(AmplitudeContext);

    if (context === undefined) {
        throw new Error('useAmplitude must be used within an AmplitudeProvider');
    }

    return context;
}
